import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingScreen } from '@/components/ui/LoadingScreen';

/**
 * `portalOnly` splits the app into two disjoint route trees: the Client
 * Portal (`/portal/*`, for the `client` role only) and everything else
 * (staff, `client` role never gets in). This is a UX convenience, not
 * the security boundary — the real one is server-side (Client-role
 * users hold zero role_permissions rows as of migration 044, and every
 * /portal/* endpoint is gated by ClientPortalMiddleware, not a
 * permission slug). A client-role user hitting a staff route by URL
 * would just get 401/403s from every API call; this redirect only
 * avoids showing them a broken staff shell.
 */
export function ProtectedRoute({
  requiredPermission,
  portalOnly = false,
  requireSuperAdmin = false,
}: {
  requiredPermission?: string;
  portalOnly?: boolean;
  /** Platform Super Admin only (users.is_super_admin) — a platform concern, not an organization permission slug. See modules/billing/routes.php's /platform-admin/* routes, the first real user of this gate. */
  requireSuperAdmin?: boolean;
}) {
  const { isAuthenticated, isLoading, hasPermission, roles, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen label="Checking your session…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const isClientPortalUser = roles.includes('client');
  const homePath = isClientPortalUser ? '/portal' : '/';

  if (portalOnly && !isClientPortalUser) {
    return <Navigate to="/" replace />;
  }
  if (!portalOnly && isClientPortalUser) {
    return <Navigate to="/portal" replace />;
  }

  if (requireSuperAdmin && !user?.is_super_admin) {
    return <Navigate to={homePath} replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={homePath} replace />;
  }

  return <Outlet />;
}
